// ── Progress tab — the week-wise participant progress grid (Wave 3, REAL read) ─
// Reuses progress.weekWise wrapped in useHonestQuery -> CohortTabShell.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import { useMemo } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingZ6Services } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingWeekWiseProgressDto } from "../../../services/coaching-z6-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { mapProgressParticipants, useCohortProgressCollection } from "./cohort-progress-collection";

export function CohortProgressTab({ cohortId }: { cohortId: string }) {
  const query = useHonestQuery<CoachingWeekWiseProgressDto>(
    coachingQueryKeys.z6.progressWeekWise(cohortId),
    () => getCoachingZ6Services().progress.weekWise(cohortId),
    { isEmpty: (data) => (data.progress?.participants ?? []).length === 0 }
  );
  const progress = query.data?.progress;
  const weeks = progress?.weeks ?? [];
  const allRows = useMemo(
    () => (progress ? mapProgressParticipants(progress) : []),
    [progress]
  );
  const collection = useCohortProgressCollection(allRows, weeks);

  return (
    <CohortTabShell
      title="Progress"
      subtitle="Week-wise participant progress. Each cell is attendance / assignment, with R when a report is available."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No progress recorded"
      emptyMessage="Per-participant weekly progress will appear here once sessions are held."
    >
      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter participants"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Week-wise progress"
          emptyTitle="No participants match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}